import {
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  InputBase,
  MenuItem,
  Select,
  Grow,
  Typography,
} from '@material-ui/core'
import { AttachFile, Clear, Send } from '@material-ui/icons'
import { Autocomplete, Skeleton } from '@material-ui/lab'
import { graphql } from 'babel-plugin-relay/macro'
import React, { Suspense, unstable_useTransition, useEffect, useMemo, useState } from 'react'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { media } from '../../theme'
import styled from 'styled-components'
import useNiceMutation from '../../mutations/useNiceMutation'
import { SendMessageDialogFormQuery } from '../../__generated__/SendMessageDialogFormQuery.graphql'
import { infoSendMessageInput, SendMessageDialogMutation } from '../../__generated__/SendMessageDialogMutation.graphql'
import Button from '../Button'
import LoadingDots from '../LoadingDots'
import OdysseyDialog from '../OdysseyDialog'
import { MessageEntityEnum } from './types'
import AttachmentSelectionDialog from './AttachmentSelectionDialog'
import EntityAttachment from '../File/EntityAttachment'
import { TransitionGroup } from 'react-transition-group'
import EntityAttachmentListPreviewer from '../File/EntityAttachmentListPreviewer'

interface Props {
  entitySlug: string
  entityType: MessageEntityEnum
  open: boolean
  onClose?: () => void
  initialTemplateSlug?: string | null
}

interface OptionType {
  name?: string
  contactNumber?: string
  contactType?: string
}

const HorizontalFlex = styled.div`
  display: flex;
  align-items: center;
`

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  padding: 0;
`

const TitleOptionsFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  min-height: 32px;
  margin-bottom: 8px;
  background-color: ${(props) => (props.theme.palette.type === 'light' ? props.theme.palette.grey[200] : props.theme.palette.grey[800])};
  padding: 0 24px;
`

const TitleOptionsSelect = styled(Select)`
  & > .MuiSelect-root {
    padding: 0 16px;
    height: 100%;
    display: flex;
    align-items: center;
    background-color: ${(props) => (props.theme.palette.type === 'light' ? props.theme.palette.grey[200] : props.theme.palette.grey[800])};
  }

  & > .MuiSelect-icon {
    right: 8px;
  }
`

const TemplateSelect = styled(TitleOptionsSelect)`
  border-right: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledInputBase = styled(InputBase)`
  height: 100%;
  width: 100%;
`

const StyledAutoCompleteInputBase = styled(StyledInputBase)`
  height: 100%;
  width: 100%;
  padding: 0 16px;

  & > .MuiAutocomplete-endAdornment {
    right: 7px;
  }
`

const FieldContainer = styled.div`
  padding: 0 24px;
`

const FieldInputContainer = styled.div`
  display: flex;
  align-items: center;
`

const FieldLabel = styled(Typography).attrs({ variant: 'body2', color: 'textSecondary' })`
  margin-right: 16px;
`

const StyledDivider = styled(Divider)`
  padding-left: 24px;
  margin: 8px 0;
`

const StyledDialogActions = styled(DialogActions)`
  display: flex;
  justify-content: space-between;
`
const AttachmentsContainer = styled.div`
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  grid-auto-rows: 120px;
  grid-gap: 16px;
  padding: 0 24px;
  margin-bottom: 16px;

  ${media.small`
    grid-template-columns: repeat(3, minmax(0, 1fr));
  `}
`

const AttachmentWrapper = styled.div`
  position: relative;
`

const AttachmentRemoveIconBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  background: linear-gradient(to bottom right, rgba(0, 0, 0, 0.25) 0%, transparent 18%);
  border-radius: 4px;
`

const AttachmentRemoveIconButton = styled(IconButton)`
  position: absolute;
  top: 0;
  left: 0;
`

const SendMessageDialog: React.FC<Props> = ({ entitySlug, entityType, open, onClose, initialTemplateSlug }) => {
  return (
    <OdysseyDialog open={open} onClose={onClose} fullWidth maxWidth='sm' style={{ maxWidth: '960px', margin: 'auto' }}>
      <DialogTitle>New Message</DialogTitle>
      <Suspense fallback={<SendMessageDialogInternal.Skeleton />}>
        <SendMessageDialogInternal
          entityType={entityType}
          entitySlug={entitySlug}
          onClose={onClose}
          initialTemplateSlug={initialTemplateSlug}
        />
      </Suspense>
    </OdysseyDialog>
  )
}

interface InternalProps {
  entitySlug: string
  entityType: MessageEntityEnum
  onClose?: () => void
  initialTemplateSlug?: string | null
  bodyContentFilter?: string[]
}

const messageSubjectTrim = (msg: string) => {
  if (!msg) return ''
  if (msg.length > 41) {
    return msg.slice(0, 18) + '...' + msg.slice(-20)
  }
  return msg
}

export const SendMessageDialogInternal: React.FC<InternalProps> & { Skeleton: React.FC } = ({
  entitySlug,
  entityType,
  onClose,
  initialTemplateSlug,
  bodyContentFilter,
}) => {
  const [templateSlug, setTemplateSlug] = useState<null | string>(initialTemplateSlug === undefined ? null : initialTemplateSlug)
  const [language, setLanguage] = useState<null | string>('en')
  const { messageForm } = useLazyLoadQuery<SendMessageDialogFormQuery>(query, {
    entitySlug: entitySlug,
    entityType: entityType,
    templateSlug: templateSlug,
    bodyContentFilter: bodyContentFilter,
  })
  const selectedMessage = useMemo(() => messageForm?.messages.find((message) => message.language === language), [messageForm, language]) || []

  const possibleReceivers = messageForm?.possibleReceivers.map((item) => ({ id: item.contactId, number: item.contactNumber }))

  const [startTemplateTransition, templateTransitionIsInProgress] = unstable_useTransition()

  const [newMessageInput, setNewMessageInput] = useState<infoSendMessageInput>({
    body: selectedMessage?.body || '',
    entitySlug: entitySlug,
    entityType: entityType,
    subject: selectedMessage?.subject || '',
    templateSlug: `${templateSlug}_${language}` || '',
    to: (selectedMessage?.to as string[]) || [],
    attachments: (selectedMessage?.attachments as string[]) || [],
    subjectParams: selectedMessage?.subjectParams as string[],
    bodyParams: selectedMessage?.bodyParams as string[],
    signature: selectedMessage?.signature || '',
    userDetails: possibleReceivers || [],
  })
  const [previewedAttachmentSlug, setPreviewedAttachmentSlug] = useState<null | string>(null)

  const selectedAttachments = useMemo(
    () =>
      messageForm?.attachmentFolderSets.flatMap((folderSet) =>
        folderSet.folders.flatMap((folder) => folder.attachments.filter((att) => newMessageInput.attachments?.includes(att.attachmentSlug)))
      ),
    [newMessageInput, messageForm]
  )

  const [bodyVariable, setBodyVariable] = useState<null | string>('')
  useEffect(() => {
    setNewMessageInput({
      body: selectedMessage?.body || '',
      entitySlug: entitySlug,
      entityType: entityType,
      subject: selectedMessage?.subject || '',
      templateSlug: `${templateSlug || messageForm?.templateSlug || ''}_${language}` || '',
      to: (selectedMessage?.to as string[]) || [],
      attachments: (selectedMessage?.attachments as string[]) || [],
      subjectParams: [messageSubjectTrim(...(selectedMessage?.subjectParams || ''))],
      bodyParams: selectedMessage?.bodyParams as string[],
      userParams: null,
      signature: selectedMessage?.signature || '',
      userDetails: possibleReceivers || [],
    })
  }, [selectedMessage, entitySlug, entityType, templateSlug])

  const [commit, sendMessageIsInFlight] = useNiceMutation<SendMessageDialogMutation>(mutation)
  const [attachmentSelectionOpen, setAttachmentSelectionOpen] = useState(false)
  const onSubmit = () => {
    const valueToAdd = bodyVariable || '-'
    const updatedState = {
      ...newMessageInput,
      userParams: valueToAdd,
    }
    setNewMessageInput(updatedState)
    commit({
      variables: {
        input: updatedState,
      },
      onCompleted: (_res, errors) => {
        if (!errors) {
          onClose && onClose()
        }
      },
    })
  }

  const handleChange = (event: any, newValue: any) => {
    setNewMessageInput((prev) => ({
      ...prev,
      to: newValue.map((val: any) => (typeof val === 'string' ? val : val.contactNumber)),
      userDetails: newValue.map((val: any) => ({
        id: val.contactId,
        number: val.contactNumber,
      })),
    }))
  }

  return (
    <>
      <StyledDialogContent dividers>
        <TitleOptionsFieldsContainer>
          <TemplateSelect
            value={templateSlug || messageForm?.templateSlug || ''}
            onChange={(e) => {
              startTemplateTransition(() => {
                setTemplateSlug(e.target.value as string)
              })
            }}
            input={
              <StyledInputBase
                fullWidth
                endAdornment={
                  templateTransitionIsInProgress ? (
                    <InputAdornment position='end' style={{ display: 'flex', marginRight: '42px' }}>
                      <LoadingDots variant='dark' />
                    </InputAdornment>
                  ) : null
                }
              />
            }
          >
            {messageForm?.possibleTemplates.map((template) => (
              <MenuItem key={template.slug} value={template.slug}>
                {template.title}
              </MenuItem>
            ))}
          </TemplateSelect>
          <TitleOptionsSelect value={language} onChange={(e) => setLanguage(e.target.value as string)} input={<StyledInputBase fullWidth />}>
            {messageForm?.messages.map((message) => (
              <MenuItem key={message.language} value={message.language}>
                {message.languageTitle}
              </MenuItem>
            ))}
          </TitleOptionsSelect>
        </TitleOptionsFieldsContainer>
        <FieldContainer>
          <Autocomplete
            multiple
            freeSolo
            disabled={sendMessageIsInFlight || templateTransitionIsInProgress}
            groupBy={(option) => (typeof option === 'string' ? '' : option.group)}
            options={[...(messageForm?.possibleReceivers || [])]}
            renderOption={(option) =>
              typeof option === 'string' ? (
                <div>
                  <Typography variant='body2'>{option}</Typography>
                </div>
              ) : (
                <div>
                  <Typography variant='body2'>
                    {option.name} - {option.contactNumber} - ({option.contactType})
                  </Typography>
                  <Typography variant='caption' color='textSecondary'>
                    {option.roles.join(', ')}
                  </Typography>
                </div>
              )
            }
            getOptionLabel={(option: OptionType) =>
              typeof option === 'string'
                ? option
                : `${option.name || ''}${option.contactNumber ? ` - ${option.contactNumber}` : ''} 
                ${option.contactType ? ` (${option.contactType})` : ''}`
            }
            value={messageForm?.possibleReceivers.filter((user) => user.roles.some((role) => selectedMessage?.roles.includes(role)))}
            renderInput={(params) => (
              <FieldInputContainer>
                <FieldLabel>To:</FieldLabel>
                <StyledAutoCompleteInputBase inputProps={params.inputProps} {...params.InputProps} />
              </FieldInputContainer>
            )}
            renderTags={(options, getTagProps) =>
              options.map((option, index) => (
                <Chip
                  key={typeof option === 'string' ? option : option.contactNumber}
                  variant='outlined'
                  label={typeof option === 'string' ? option : `${option.name} - ${option.contactNumber} - (${option.contactType})`}
                  {...getTagProps({ index })}
                  deleteIcon={<Clear />}
                />
              ))
            }
            onChange={(event, newValue: any) => {
              handleChange(event, newValue)
            }}
          />
        </FieldContainer>
        <StyledDivider />
        <FieldContainer>
          <FieldInputContainer>
            <FieldLabel>Subject:</FieldLabel>
            <StyledInputBase
              value={newMessageInput.subject}
              disabled={true}
              onChange={(evt) => setNewMessageInput((prevState) => ({ ...prevState, subject: evt.target.value }))}
            />
          </FieldInputContainer>
        </FieldContainer>
        <StyledDivider />
        <FieldContainer>
          <FieldLabel>Body:</FieldLabel>
          <StyledInputBase
            multiline
            rows={10}
            rowsMin={10}
            // placeholder='Type body here...'
            value={newMessageInput.body}
            disabled={true}
          />
        </FieldContainer>
        <FieldContainer>
          <StyledInputBase
            multiline
            rows={10}
            rowsMin={10}
            placeholder='Type more here...'
            value={bodyVariable}
            disabled={sendMessageIsInFlight || templateTransitionIsInProgress}
            onChange={(evt) => setBodyVariable(evt.target.value)}
          />
        </FieldContainer>
        {newMessageInput.signature && (
          <FieldContainer>
            <StyledInputBase
              multiline
              rows={10}
              rowsMin={10}
              value={newMessageInput.signature}
              disabled={true}
              onChange={(evt) => setNewMessageInput((prevState) => ({ ...prevState, signature: evt.target.value }))}
            />
          </FieldContainer>
        )}
        <AttachmentsContainer>
          <TransitionGroup component={null}>
            {selectedAttachments?.map((attachment) => (
              <Grow in={true} key={attachment.attachmentSlug} style={{ transformOrigin: 'center top' }}>
                <AttachmentWrapper>
                  <EntityAttachment attachment={attachment} onPreview={() => setPreviewedAttachmentSlug(attachment.attachmentSlug)} />
                  <AttachmentRemoveIconBackdrop />
                  <AttachmentRemoveIconButton
                    size='small'
                    onClick={() =>
                      setNewMessageInput((prevState) => ({
                        ...prevState,
                        attachments: prevState.attachments?.filter((slug) => slug !== attachment.attachmentSlug),
                      }))
                    }
                  >
                    <Clear fontSize='small' htmlColor='white' />
                  </AttachmentRemoveIconButton>
                </AttachmentWrapper>
              </Grow>
            ))}
          </TransitionGroup>
        </AttachmentsContainer>
      </StyledDialogContent>
      <StyledDialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <HorizontalFlex>
          <IconButton onClick={() => setAttachmentSelectionOpen(true)} disabled={sendMessageIsInFlight}>
            <AttachFile />
          </IconButton>
          <Button
            variant='contained'
            onClick={onSubmit}
            startIcon={<Send />}
            disabled={
              sendMessageIsInFlight ||
              !(messageForm?.possibleReceivers && messageForm.possibleReceivers.length > 0) ||
              newMessageInput.to.length === 0
            }
          >
            {sendMessageIsInFlight ? <LoadingDots /> : 'Send'}
          </Button>
        </HorizontalFlex>
      </StyledDialogActions>
      <AttachmentSelectionDialog
        emailForm={messageForm}
        selectedAttachmentSlugs={newMessageInput.attachments || []}
        open={attachmentSelectionOpen}
        onClose={() => setAttachmentSelectionOpen(false)}
        onSelection={(attachmentSlugs) => {
          setNewMessageInput((prevState) => ({ ...prevState, attachments: attachmentSlugs }))
          setAttachmentSelectionOpen(false)
        }}
      />
      <EntityAttachmentListPreviewer
        attachments={selectedAttachments}
        intialPreviewedAttachmentSlug={previewedAttachmentSlug}
        open={!!previewedAttachmentSlug}
        onClose={() => setPreviewedAttachmentSlug(null)}
        attachmentsEditable={false}
      />
    </>
  )
}

SendMessageDialogInternal.Skeleton = () => (
  <>
    <StyledDialogContent dividers>
      <TitleOptionsFieldsContainer>
        <div style={{ height: '31px' }}></div>
      </TitleOptionsFieldsContainer>
      <FieldContainer>
        <FieldInputContainer>
          <FieldLabel>To:</FieldLabel>
          <Skeleton>
            <Typography variant='body1'>................................</Typography>
          </Skeleton>
        </FieldInputContainer>
      </FieldContainer>
      <StyledDivider />
      <FieldContainer>
        <FieldInputContainer>
          <FieldLabel>Subject:</FieldLabel>
          <Skeleton>
            <Typography variant='body1'>................................</Typography>
          </Skeleton>
        </FieldInputContainer>
      </FieldContainer>
      <StyledDivider />
      <FieldContainer style={{ marginBottom: '16px' }}>
        <Skeleton variant='rect' height='190px'></Skeleton>
      </FieldContainer>
    </StyledDialogContent>
    <StyledDialogActions>
      <Button disabled>Cancel</Button>
      <HorizontalFlex>
        <IconButton disabled>
          <AttachFile />
        </IconButton>
        <Button variant='contained' startIcon={<Send />} disabled>
          Send
        </Button>
      </HorizontalFlex>
    </StyledDialogActions>
  </>
)

const query = graphql`
  query SendMessageDialogFormQuery(
    $entityType: MessageEntityTypeEnum!
    $entitySlug: String!
    $templateSlug: String
    $bodyContentFilter: [String!]
  ) {
    messageForm(entityType: $entityType, entitySlug: $entitySlug, templateSlug: $templateSlug, bodyContentFilter: $bodyContentFilter) {
      entityType
      entitySlug
      templateSlug
      defaultLanguage
      messages {
        language
        languageTitle
        subject
        to
        roles
        body
        attachments
        subjectParams
        bodyParams
        signature
      }
      possibleTemplates {
        title
        slug
      }

      possibleReceivers {
        contactId
        contactNumber
        contactType
        roles
        name
        group
      }

      attachmentFolderSets {
        folders {
          slug
          name
          attachments {
            attachmentSlug
            ...EntityAttachmentListPreviewer_attachment
            ...EntityAttachment_attachment
          }
        }
      }
      ...AttachmentSelectionDialog_messageForm
    }
  }
`

const mutation = graphql`
  mutation SendMessageDialogMutation($input: infoSendMessageInput!) {
    infoSendMessage(input: $input) {
      clientMutationId
    }
  }
`

export default SendMessageDialog
